import { useNavigate, useSearchParams } from '@ksyos/router';
import { get } from 'lodash';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import type { IRequest } from '../../../../common/interfaces/IRequest';
import type {
    IEssQuestionnaireCustomControlData,
    IFosq10QuestionnaireCustomControlData,
    IHsdqQuestionnaireCustomControlData,
    IQuestionnaireProcedureAddValidated,
    QuestionnaireCustomControlData,
    QuestionnaireCustomControlKeys,
} from '../../../../common/validation/customControls/somnology';
import type { IReduxState } from '../../../configureStore';
import type { ValidationErrors } from '../../../utils/validation';
import type { IOSASQuestionnaire, IOSASWithQuestionnaires } from './NewOsasQuestionnaires';
import { OsasQuestionnaireName } from '../../../../common/enums/OsasQuestionnaireName';
import { ServiceCode } from '../../../../common/enums/ServiceCode';
import { questionnaireValidationTypes, stepSchemas } from '../../../../common/validation/customControls/somnology';
import { fetchJson } from '../../../store/fetch';
import { emptyValidationErrors, validate } from '../../../utils/validation';
import { scrollToValidationError } from '../../elements/scrollToValidationError';
import { NewEssQuestionnaire } from './questionnaires/EssQuestionnaire/NewEssQuestionnaire';
import { NewFosq10Questionnaire } from './questionnaires/Fosq10Questionnaire/NewFosq10Questionnaire';
import { NewHsdqQuestionnaire } from './questionnaires/HsdqQuestionnaire/NewHsdqQuestionnaire';

export interface IQuestionnaireFormProps {
    currentStep: number;
    errors: ValidationErrors;
    goToNextStep: (stepsLength: number) => void;
    goToPrevStep: () => void;
    handleChange: (update: Partial<IQuestionnaireProcedureAddValidated>) => void;
    isSubmitting?: boolean;
    onUploadStatusChanged?: (isUploading: boolean) => void;
    procedure: IQuestionnaireProcedureAddValidated;
    submit?: () => Promise<void>;
    validateAndGoToNextStep: () => void;
    validateAndSubmitQuestionnaire: () => Promise<void>;
}

interface IQuestionnaireReduxProps {
    osasExaminationRequest?: IOSASWithQuestionnaires;
}

const mapStateToProps = ({ authentication }: IReduxState): IQuestionnaireReduxProps => ({
    osasExaminationRequest: authentication.requestProcesses?.find((request: IRequest) =>
        [ServiceCode.OSASO, ServiceCode.OSASVO, ServiceCode.OSASQ].includes(request.service.code)
    ),
});

const questionsPerQuestionnaireStep = {
    [OsasQuestionnaireName.ess]: 4,
    [OsasQuestionnaireName.fosq10]: 4,
    [OsasQuestionnaireName.hsdq]: 5,
};

export function QuestionnaireWrapper(props: IQuestionnaireReduxProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const questionnaireName = searchParams.get('name') as OsasQuestionnaireName;
    const procedureId = searchParams.get('procedureId') as string;
    const orderedQuestions: QuestionnaireCustomControlKeys[] =
        getOrderedQuestionsForQuestionnaireName(questionnaireName);
    const numberOfSteps = questionnaireName === OsasQuestionnaireName.hsdq ? 8 : 3;
    const [currentStep, setCurrentStep] = useState<number>(-1);
    const [firstErrorInForm, setFirstErrorInForm] = useState<string | null>(null);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [areValidationsActivated, setAreValidationsActivated] = useState<boolean>(false);
    const [procedure, setProcedure] = useState<IQuestionnaireProcedureAddValidated>({
        customControlData: getEmptyAnswersForQuestionnaire(questionnaireName),
        procedureId,
    });

    useEffect(() => {
        if (props.osasExaminationRequest) {
            loadQuestionnaire();
        }
    }, [props.osasExaminationRequest]);

    useEffect(() => {
        if (areValidationsActivated) {
            validateOnChange();
        }
    }, [procedure]);

    useEffect(() => {
        if (firstErrorInForm) {
            scrollToValidationError(firstErrorInForm);
        }
    }, [errors]);

    function loadQuestionnaire() {
        const questionnaire = getQuestionnaire();
        if (questionnaire) {
            if (isQuestionnaireSent(questionnaire)) {
                window.location.href = `/osaso/questionnaires?procedureId=${procedureId}`;
            } else {
                setCurrentStep(getCurrentStep(questionnaire));
                setProcedure({
                    ...procedure,
                    customControlData: questionnaire.answer,
                });
            }
        } else {
            setCurrentStep(0);
        }
    }

    function getQuestionnaire() {
        const sentQuestionnaires = props.osasExaminationRequest?.procedure?.customControlData?.questionnaires;
        if (sentQuestionnaires) {
            return sentQuestionnaires.find((questionnaire) => questionnaire.name === questionnaireName);
        }

        return undefined;
    }

    function isQuestionnaireSent(questionnaire: IOSASQuestionnaire) {
        return Object.values(questionnaire.answer).every((value) => value !== null);
    }

    function getCurrentStep(questionnaire: IOSASQuestionnaire) {
        const numberOfFirstQuestionNotAnswered = orderedQuestions
            .map((item) => get(questionnaire.answer, item))
            .indexOf(undefined);
        const step =
            Math.floor(numberOfFirstQuestionNotAnswered / questionsPerQuestionnaireStep[questionnaireName]) + 1;

        return step;
    }

    function getStepValidationSchema() {
        return get(stepSchemas, [questionnaireName, currentStep], null);
    }

    function getValidationSchemaByMode(mode: 'draft' | 'final') {
        return get(questionnaireValidationTypes, [questionnaireName, mode], null);
    }

    async function validateAndGoToNextStep() {
        setAreValidationsActivated(true);
        const schema = getStepValidationSchema();
        if (schema) {
            const validationResult = validate(schema, procedure.customControlData);
            if (validationResult.errors.size === 0) {
                await save('draft');
                setFirstErrorInForm(null);
                goToNextStep();
            } else {
                setErrors(validationResult.errors);
                setFirstErrorInForm(validationResult.errors.keySeq().toArray()[0]);
            }
        } else {
            goToNextStep();
        }
    }

    function validateOnChange() {
        const schema = getStepValidationSchema();
        if (schema) {
            const validationResult = validate(schema, procedure.customControlData);
            setErrors(validationResult.errors);
            setFirstErrorInForm(null);
        }
    }

    function goToPrevStep() {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        } else {
            navigate(-1);
        }
    }

    function goToNextStep() {
        setAreValidationsActivated(false);
        setErrors(emptyValidationErrors);
        if (currentStep < numberOfSteps - 1) {
            setCurrentStep(currentStep + 1);
        }
    }

    function handleChange(update: Partial<IQuestionnaireProcedureAddValidated>) {
        setProcedure({ ...procedure, customControlData: { ...procedure.customControlData, ...update } });
    }

    async function save(mode: 'draft' | 'final') {
        const validationType = getValidationSchemaByMode(mode);
        const result = await fetchJson('/api/patient/osasQuestionnaire', 'POST', {
            procedureId: procedure.procedureId,
            questionnaire: {
                answer: procedure.customControlData,
                name: questionnaireName,
            },
            validationType,
        });

        if (result.status === 200 && mode === 'final') {
            window.location.href = `/osaso/questionnaires?procedureId=${procedure.procedureId}`;
        }
    }

    async function validateAndSubmitQuestionnaire() {
        setAreValidationsActivated(true);
        const schema = getStepValidationSchema();
        if (schema) {
            const validationResult = validate(schema, procedure.customControlData);
            if (validationResult.errors.size === 0) {
                await save('final');
            } else {
                setErrors(validationResult.errors);
                setFirstErrorInForm(validationResult.errors.keySeq().toArray()[0]);
            }
        }
    }

    function getOrderedQuestionsForQuestionnaireName(name: OsasQuestionnaireName) {
        switch (name) {
            case OsasQuestionnaireName.ess:
                return [
                    'whenReading',
                    'whenWatchingTv',
                    'inAPublicEnvironment',
                    'sittingInACarAsPassenger',
                    'restingInTheAfternoon',
                    'talkingToSomeone',
                    'afterLunch',
                    'waitingInTheCarInTraffic',
                ] as (keyof IEssQuestionnaireCustomControlData)[];
            case OsasQuestionnaireName.fosq10:
                return [
                    'difficultyConcentratingOnWork',
                    'difficultyRememberingThings',
                    'difficultyDrivingAVehicleShortDistances',
                    'difficultyDrivingAVehicleLongDistances',
                    'difficultyVisitingFriendsAndFamily',
                    'haveRelationshipsDeteriorated',
                    'difficultyWatchingVideoOrFilm',
                    'difficultyBeingActiveAtNight',
                    'difficultyBeingActiveInTheMorning',
                    'needForIntimacy',
                ] as (keyof IFosq10QuestionnaireCustomControlData)[];
            case OsasQuestionnaireName.hsdq:
                return [
                    'fatigueDuringTheDay',
                    'annoyingItchy',
                    'dryMouth',
                    'screaming',
                    'difficultyWakingUp',
                    'restlessLegs',
                    'poorSleep',
                    'urgeToMoveLegs',
                    'moveLegs',
                    'lieAwake',
                    'kickingLegMovements',
                    'concerned',
                    'troublesFallingAsleep',
                    'consequencesAfterBadNight',
                    'iDontGetEnoughSleep',
                    'vividDream',
                    'stopBreathing',
                    'snoreLoudly',
                    'suffocation',
                    'nightmares',
                    'functionLessWell',
                    'sometimesInjured',
                    'fallAsleepRepeatedly',
                    'confusedHalfAwake',
                    'iDontFeelRestedAfterNap',
                    'normalTime',
                    'sleepBadlyDuringDay',
                    'moreThanTenHours',
                    'sleepAttacks',
                    'timeVaries',
                    'sleepWalking',
                    'unintentionallyFallAsleepDuringDay',
                ] as (keyof IHsdqQuestionnaireCustomControlData)[];
            default:
                throw new Error('Ordered questions not defined for questionnaire: ' + name);
        }
    }

    function getEmptyAnswersForQuestionnaire(name: OsasQuestionnaireName): QuestionnaireCustomControlData {
        switch (name) {
            case OsasQuestionnaireName.ess:
                return {
                    afterLunch: null,
                    inAPublicEnvironment: null,
                    restingInTheAfternoon: null,
                    sittingInACarAsPassenger: null,
                    talkingToSomeone: null,
                    waitingInTheCarInTraffic: null,
                    whenReading: null,
                    whenWatchingTv: null,
                };
            case OsasQuestionnaireName.fosq10:
                return {
                    difficultyBeingActiveAtNight: null,
                    difficultyBeingActiveInTheMorning: null,
                    difficultyConcentratingOnWork: null,
                    difficultyDrivingAVehicleLongDistances: null,
                    difficultyDrivingAVehicleShortDistances: null,
                    difficultyRememberingThings: null,
                    difficultyVisitingFriendsAndFamily: null,
                    difficultyWatchingVideoOrFilm: null,
                    haveRelationshipsDeteriorated: null,
                    needForIntimacy: null,
                };
            case OsasQuestionnaireName.hsdq:
                return {
                    annoyingItchy: null,
                    concerned: null,
                    confusedHalfAwake: null,
                    consequencesAfterBadNight: null,
                    difficultyWakingUp: null,
                    dryMouth: null,
                    fallAsleepRepeatedly: null,
                    fatigueDuringTheDay: null,
                    functionLessWell: null,
                    iDontFeelRestedAfterNap: null,
                    iDontGetEnoughSleep: null,
                    kickingLegMovements: null,
                    lieAwake: null,
                    moreThanTenHours: null,
                    moveLegs: null,
                    nightmares: null,
                    normalTime: null,
                    poorSleep: null,
                    restlessLegs: null,
                    screaming: null,
                    sleepAttacks: null,
                    sleepBadlyDuringDay: null,
                    sleepWalking: null,
                    snoreLoudly: null,
                    sometimesInjured: null,
                    stopBreathing: null,
                    suffocation: null,
                    timeVaries: null,
                    troublesFallingAsleep: null,
                    unintentionallyFallAsleepDuringDay: null,
                    urgeToMoveLegs: null,
                    vividDream: null,
                };
            default:
                throw new Error('Answers not defined for questionnaire' + name);
        }
    }

    const questionnaireProps = {
        currentStep,
        errors,
        goToNextStep,
        goToPrevStep,
        handleChange,
        procedure,
        validateAndGoToNextStep,
        validateAndSubmitQuestionnaire,
    };

    if (questionnaireName === OsasQuestionnaireName.ess) {
        return <NewEssQuestionnaire {...questionnaireProps} />;
    } else if (questionnaireName === OsasQuestionnaireName.fosq10) {
        return <NewFosq10Questionnaire {...questionnaireProps} />;
    } else if (questionnaireName === OsasQuestionnaireName.hsdq) {
        return <NewHsdqQuestionnaire {...questionnaireProps} />;
    }

    return null;
}

export const Questionnaire = connect(mapStateToProps)(QuestionnaireWrapper);
